import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/lib/api';
import { ErrorText, PageHeader } from '@/components/ui';
import { PosterChoices } from '@/components/poster-card';

type Kind = 'quiz' | 'course';

/**
 * Campaign posters for a quiz or course: one page with a QR code to it, for
 * notice boards (light, to print) and social media or mailing lists (dark).
 * Rendered on demand from the published quiz or course.
 */
export function QuizPosterPage() {
  return <PosterPage kind="quiz" />;
}

export function CoursePosterPage() {
  return <PosterPage kind="course" />;
}

function PosterPage({ kind }: { kind: Kind }) {
  const { slug = '' } = useParams();
  const api = useApi();
  const info = useQuery({
    queryKey: [kind === 'quiz' ? 'publicQuiz' : 'publicCourse', slug],
    queryFn: async (): Promise<{ title: string }> =>
      kind === 'quiz' ? api.publicQuiz(slug) : api.publicCourse(slug),
    retry: false,
  });

  if (info.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (info.isError) return <ErrorText error={info.error} />;

  return (
    <div>
      <PageHeader
        title="Campaign poster"
        description={`A one-page poster for “${info.data.title}” with a QR code to the ${kind}. Print it for notice boards and common areas, or share the dark version online.`}
        back={{
          to: `/${kind === 'quiz' ? 'q' : 'c'}/${slug}`,
          label: kind === 'quiz' ? 'Quiz' : 'Course',
        }}
      />
      <PosterChoices
        source={{
          key: [kind, slug],
          file: (theme, format, size = 'letter') => api.posterFile(slug, theme, format, size, kind),
          fileName: (theme, d) =>
            `Nirvik-Ready-${kind === 'quiz' ? 'Poster' : 'Course-Poster'}-${slug}-${theme}${
              d.format === 'pdf' ? `-${d.size ?? 'letter'}` : ''
            }`,
        }}
      />
    </div>
  );
}
